/**
 * Reine Helfer fuer die ZEO-Arbeitsoberflaeche (Klient-/Aufgabenauswahl, Context Bar, Dokumentkarte).
 * Bewusst ohne React-/Router-Import (isoliert testbar). Zeigt NIE interne IDs an -- nur Namen/Titel.
 */
export const BUERO_ALLGEMEIN = 'Büro allgemein';

export function leererKontext() {
  return { klient: BUERO_ALLGEMEIN, aufgabe: null, dokument: null, vorgang: null, klient_gebunden: false };
}

/** Zeilen der Context Bar. Aufgabe/Dokument erscheinen immer ("keine"), Vorgang nur wenn vorhanden. */
export function kontextBarZeilen(kontext) {
  const k = kontext || leererKontext();
  const zeilen = [
    { key: 'klient', label: 'Klient', wert: k.klient || BUERO_ALLGEMEIN },
    { key: 'aufgabe', label: 'Aufgabe', wert: k.aufgabe || 'keine' },
  ];
  if (k.vorgang) zeilen.push({ key: 'vorgang', label: 'Vorgang', wert: k.vorgang });
  if (k.dokument) zeilen.push({ key: 'dokument', label: 'Dokument', wert: k.dokument });
  return zeilen;
}

/** Aufgaben/Neue Aufgabe sind nur mit gebundenem Klienten moeglich (Buero allgemein: nein). */
export function kannAufgabeWaehlen(kontext) {
  return !!(kontext && kontext.klient_gebunden);
}

/** Payload fuer POST /zeo-workspace/session. klientId=null -> Buero allgemein (keine Aufgabe/Vorgang). */
export function sessionStartPayload({ klientId, taskId, vorgangId }) {
  if (!klientId) return { klient_id: null };
  const payload = { klient_id: klientId };
  if (taskId) payload.task_id = taskId;
  if (vorgangId) payload.vorgang_id = vorgangId;
  return payload;
}

/** Payload fuer PUT /session/{id}/entities: Aufgabe/Vorgang binden oder (leer) loesen. */
export function entitiesPayload({ taskId, vorgangId }) {
  const payload = {};
  if (taskId) payload.task_id = taskId;
  if (vorgangId) payload.vorgang_id = vorgangId;
  const loesen = [];
  if (!taskId) loesen.push('task');
  if (!vorgangId) loesen.push('vorgang');
  if (loesen.length) payload.loesen = loesen;
  return payload;
}

/** URL-Scope zum Chat: der Scope der Seite folgt dem Chat, nicht umgekehrt. */
export function scopeParamsFuer(klientId) {
  return klientId ? { scope: 'client', klient_id: klientId } : { scope: 'office' };
}

/** Zeilen der Dokumentkarte: Titel, Datum, Dokumentart, Klient -- keine technische ID. */
const alsDatum = (iso) => (/^\d{4}-\d{2}-\d{2}/.test(iso || '') ? `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}` : null);

/** Eindeutige Datumsangaben der Karte (31.10.2026): jedes Datum mit Bezeichnung und Herkunft; die Erfassungszeit ist nie das Dokumentdatum. */
export function datumsZeilen(ref) {
  const r = ref || {};
  const neu = ['dokumentdatum', 'eingangsdatum', 'ausgangsdatum', 'erfasst_am'].some((k) => k in r);
  if (!neu) return r.datum ? [`Datum: ${alsDatum(r.datum) || r.datum}`] : [];      // aeltere Nachrichten ohne die neuen Felder
  const q = r.datum_quelle ? ` (${r.datum_quelle})` : '';
  return [
    `Dokumentdatum: ${alsDatum(r.dokumentdatum) || 'nicht erfasst'}`,
    r.eingangsdatum ? `Eingang: ${alsDatum(r.eingangsdatum)}${q}` : null,
    r.ausgangsdatum ? `Ausgang: ${alsDatum(r.ausgangsdatum)}${q}` : null,
    r.erfasst_am ? `in Olympus erfasst: ${alsDatum(r.erfasst_am)}` : null,
  ].filter(Boolean);
}

/** Weitere Treffer nach Stufe: B = relevante Vorgangsunterlagen (eingeklappt), C = sonstige Suchtreffer (verborgen). Ohne Stufe (alt) = B. */
export function teileWeitere(refs) {
  const liste = refs || [];
  return { relevant: liste.filter((r) => r.stufe !== 'C'), sonstige: liste.filter((r) => r.stufe === 'C') };
}

export function dokumentKartenInfo(ref) {
  const r = ref || {};
  return {
    titel: r.titel || 'Dokument',
    datum: r.datum || '–',
    datumsZeilen: datumsZeilen(r),
    art: r.dokumentart_text || r.dokumentart || r.dokumententyp || r.kategorie || r.quellenart || null,
    klient: r.klient_name || null,
    // lautet das Dokument auf eine ANDERE Person als den Klienten (Pilot-Befund: Dokument einer Haushalts-/Drittperson im Akt)?
    betrifftPerson: r.betrifft_person || null,
    betrifftBeleg: r.betrifft_person_beleg || null,
    // false = Original NICHT aufloesbar (Karte bietet dann kein "Dokument oeffnen" an); unbekannt (undefined) = versuchen
    originalVerfuegbar: r.original_verfuegbar !== false,
  };
}

// ======================================================================================================
// Auswahl-Zustand des Dialogs (P0 Context Integrity 07.10.2026, Fehler 1): zu jedem Zeitpunkt EXAKT EINE aktive klient_id.
// Alles Klientengebundene (Aufgaben, Vorgaenge, Aufgabe/Vorgang-Auswahl) traegt die klient_id, fuer die es geladen wurde, und wird
// NUR angezeigt, wenn sie der aktiven entspricht. Spaete Antworten fuer einen frueheren Klienten/Suchtext werden verworfen.
// ======================================================================================================
export function startAuswahl(vorbelegung = {}) {
  return {
    suche: '', treffer: [], dropdownOffen: true,
    klient: null,                                   // {id, name} | null = Büro allgemein
    aufgaben: { klientId: null, liste: [] }, vorgaenge: { klientId: null, liste: [] },
    taskId: vorbelegung.taskId || '', vorgangId: vorbelegung.vorgangId || '', neueAufgabe: false,
  };
}

export function auswahlReducer(state, aktion) {
  switch (aktion.type) {
    case 'SUCHE':
      return { ...state, suche: aktion.text, dropdownOffen: true };
    case 'TREFFER_GELADEN':                         // nur fuer den AKTUELLEN Suchtext, nur solange kein Klient gewaehlt ist
      if (aktion.suche !== state.suche || state.klient) return state;
      return { ...state, treffer: aktion.liste };
    case 'KLIENT_GEWAEHLT':                         // alles des alten Klienten verwerfen
      return { ...startAuswahl(), klient: { id: aktion.klient.id, name: aktion.klient.name }, dropdownOffen: false };
    case 'KLIENT_ENTFERNT':                         // zurueck zur Auswahl (oder Buero allgemein)
      return startAuswahl();
    case 'AUFGABEN_GELADEN':
      if (!state.klient || state.klient.id !== aktion.klientId) return state;
      return { ...state, aufgaben: { klientId: aktion.klientId, liste: aktion.liste } };
    case 'VORGAENGE_GELADEN':
      if (!state.klient || state.klient.id !== aktion.klientId) return state;
      return { ...state, vorgaenge: { klientId: aktion.klientId, liste: aktion.liste } };
    case 'TASK_GEWAEHLT':
      return { ...state, taskId: aktion.taskId || '' };
    case 'VORGANG_GEWAEHLT':
      return { ...state, vorgangId: aktion.vorgangId || '' };
    case 'NEUE_AUFGABE_UMSCHALTEN':
      return { ...state, neueAufgabe: !!aktion.an };
    default:
      return state;
  }
}

/** Trefferliste nur sichtbar, solange kein Klient gewaehlt ist und der Dropdown offen ist. */
export function sichtbareTreffer(state) {
  return state.klient || !state.dropdownOffen ? [] : state.treffer;
}

/** Klientengebundene Listen nur fuer die AKTIVE klient_id (sonst leer -- fail closed). */
export function aktiveListe(gebundeneListe, aktiveKlientId) {
  return aktiveKlientId && gebundeneListe && gebundeneListe.klientId === aktiveKlientId ? gebundeneListe.liste : [];
}

/** Alle im Dialog sichtbaren Klienten-/Aufgaben-/Vorgangsnamen (fuer den Regressionstest "kein alter Klient mehr sichtbar"). */
export function sichtbareNamen(state, aktiveKlientId) {
  return [
    ...(state.klient ? [state.klient.name] : []), state.suche,
    ...sichtbareTreffer(state).map((k) => k.name),
    ...aktiveListe(state.aufgaben, aktiveKlientId).map((a) => a.titel),
    ...aktiveListe(state.vorgaenge, aktiveKlientId).map((v) => v.titel),
  ].filter(Boolean);
}

/**
 * Chat-Liste filtern (Pilot-Befund 07.10.2026): Titel, Klienten-Badge ODER serverseitiger Inhaltstreffer.
 * Mit `scopeKlientId` (Suche innerhalb eines Klienten) erscheinen NUR Chats dieses Klienten -- nie ein Chat eines anderen Klienten.
 * Ohne Suchtext bleibt die Liste unveraendert (der Scope wirkt nur auf Suchergebnisse).
 */
export function filtereSessions(sessions, suche, inhaltTreffer, scopeKlientId, badgeFn) {
  const q = (suche || '').trim().toLowerCase();
  const liste = Array.isArray(sessions) ? sessions : [];
  if (!q) return liste;
  const imScope = scopeKlientId
    ? liste.filter((s) => s.kontext_typ === 'betreuungsakte' && s.kontext_id === scopeKlientId)
    : liste;
  const ids = new Set((inhaltTreffer || []).map((t) => t.id));
  return imScope.filter((s) => (s.titel || '').toLowerCase().includes(q)
    || (badgeFn ? String(badgeFn(s) || '').toLowerCase().includes(q) : false)
    || ids.has(s.id));
}

export function snippetFuer(inhaltTreffer, sessionId) {
  const t = (inhaltTreffer || []).find((x) => x.id === sessionId && x.treffer === 'inhalt');
  return t ? t.snippet : null;
}

/**
 * ZEO "oeffne diese Aufgabe" (Batch 2, 07.10.2026): die UI-Aktion OPEN_TASK oeffnet die KANONISCHE Olympus-Aufgabenmaske (/aufgaben/<id>) -- keine ZEO-Schattenansicht.
 * Der Chat bleibt im ZEO-Tab erhalten (die Maske oeffnet in einem eigenen Browser-Tab), active_task_id/active_client_id bleiben serverseitig gesetzt.
 */
export function aufgabeUrl(taskId) {
  return taskId ? `/aufgaben/${encodeURIComponent(taskId)}` : null;
}

export function istOpenTask(uiAction) {
  return !!uiAction && uiAction.action === 'OPEN_TASK' && !!uiAction.task_id;
}

/** Oeffnet die Aufgabenmaske; true = geoeffnet, false = vom Browser blockiert (dann zeigt die Karte den Button "Aufgabe oeffnen"). */
export function oeffneAufgabeFenster(taskId, fenster = typeof window !== 'undefined' ? window : null) {
  const url = aufgabeUrl(taskId);
  if (!url || !fenster || typeof fenster.open !== 'function') return false;
  const w = fenster.open(url, 'olympus-aufgabe');
  return !!w;
}

/** Zeilen der Aufgabenkarte -- keine technische ID. */
export function aufgabenKartenInfo(ref) {
  const r = ref || {};
  return { titel: r.titel || 'Aufgabe', status: r.status || null, prioritaet: r.prioritaet || null, faellig: r.faellig_am ? String(r.faellig_am).slice(0, 10) : null, klient: r.klient_name || null };
}


/** UI-Aktion OPEN_DOCUMENT: liefert die Dokument-Referenz (Karte) der Nachricht, die geoeffnet werden soll -- sonst null. */
export function dokumentFuerUiAktion(msg) {
  const a = msg && msg.ui_action;
  if (!a || a.action !== 'OPEN_DOCUMENT' || !a.document_id) return null;
  const refs = Array.isArray(msg.document_refs) ? msg.document_refs : [];
  return refs.find((r) => r && r.id === a.document_id) || null;
}
